/**
 * Where a visit to a public CV came from, in one vocabulary the share buttons,
 * the web beacon, the API and the owner's analytics screen all agree on.
 *
 * Three signals, strongest first:
 * 1. `utm_source`, for links someone tagged on purpose.
 * 2. `ref`, which Tabbio's own share buttons add (`?ref=whatsapp`). In-app
 *    browsers (WhatsApp, LinkedIn, Instagram) and QR scans send no referrer, so
 *    without it most shared traffic would read as "direct".
 * 3. The referrer's host, mapped to a named source where we know it.
 *
 * Nothing here reads a search keyword: search engines stopped sending the
 * query in the referrer years ago, so a Google visit is known only as Google.
 */

export const CV_SHARE_REF_PARAM = "ref";

/**
 * The fragment the owner's own app adds when it opens their public CV. The
 * page's beacon sees it, remembers this browser as the owner's, strips it from
 * the address bar and skips the view. A fragment never reaches the server, so
 * the page stays cacheable and the marker never leaks through a Referer.
 */
export const CV_OWNER_VIEW_HASH = "tabbio-owner";

export type CvShareChannel =
  | "copy"
  | "share"
  | "qr"
  | "email"
  | "whatsapp"
  | "linkedin"
  | "x"
  | "facebook"
  | "instagram"
  | "telegram"
  | "tiktok"
  | "snapchat"
  | "youtube"
  | "google_business";

/** Platform ids used around the apps that name the same destination. */
const CHANNEL_ALIASES: Record<string, string> = {
  linkedin_page: "linkedin",
  facebook_page: "facebook",
  twitter: "x",
  copy_link: "copy",
  native_share: "share",
  mail: "email",
  gmail: "email",
  wa: "whatsapp",
  tg: "telegram",
  ig: "instagram",
  fb: "facebook",
  li: "linkedin",
};

const SAFE_TOKEN = /^[a-z0-9][a-z0-9_-]{0,31}$/;

/**
 * A `ref` / `utm_source` value reduced to a short, safe token, or null.
 * Unknown tokens are kept (an owner may tag their own newsletter link), but
 * anything that is not a plain slug is dropped, never stored.
 */
export function normalizeTrafficToken(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const lowered = value.trim().toLowerCase().replace(/\s+/g, "_");
  if (!lowered || !SAFE_TOKEN.test(lowered)) return null;
  return CHANNEL_ALIASES[lowered] ?? lowered;
}

/**
 * `url` with `?ref=<channel>` set. Returns the input unchanged when it is not
 * an absolute http(s) URL, so a missing public link never turns into garbage.
 */
export function withCvShareRef(url: string, channel: CvShareChannel | string): string {
  const token = normalizeTrafficToken(channel);
  if (!token) return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return url;
    parsed.searchParams.set(CV_SHARE_REF_PARAM, token);
    return parsed.toString();
  } catch {
    return url;
  }
}

/** `url` with the owner marker fragment, for the owner's own "open" buttons. */
export function withOwnerViewMarker(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return url;
    parsed.hash = CV_OWNER_VIEW_HASH;
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * The attribution half of a landing page's query string: `utm_*` and `ref`
 * only. Everything else (tokens, handles, whatever a partner appended) stays
 * on the page and never reaches analytics.
 */
export function pickLandingAttribution(search: string): string {
  const input = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const output = new URLSearchParams();
  for (const key of ["utm_source", "utm_medium", "utm_campaign", CV_SHARE_REF_PARAM]) {
    const value = input.get(key)?.trim();
    if (value) output.set(key, value.slice(0, 120));
  }
  const serialized = output.toString();
  return serialized ? `?${serialized}` : "";
}

type HostRule = { source: string; hosts: string[] };

/** Ordered: mail hosts before the search engines that share their domain. */
const HOST_RULES: HostRule[] = [
  {
    source: "email",
    hosts: [
      "mail.google.com",
      "com.google.android.gm",
      "outlook.live.com",
      "outlook.office.com",
      "outlook.office365.com",
      "mail.yahoo.com",
      "mail.proton.me",
      "mail.zoho.com",
    ],
  },
  { source: "google", hosts: ["com.google.android.googlequicksearchbox"] },
  { source: "bing", hosts: ["bing.com"] },
  { source: "duckduckgo", hosts: ["duckduckgo.com"] },
  { source: "yahoo", hosts: ["search.yahoo.com", "yahoo.com"] },
  { source: "ecosia", hosts: ["ecosia.org"] },
  { source: "brave", hosts: ["search.brave.com"] },
  { source: "baidu", hosts: ["baidu.com"] },
  { source: "linkedin", hosts: ["linkedin.com", "lnkd.in", "com.linkedin.android"] },
  {
    source: "whatsapp",
    hosts: ["whatsapp.com", "wa.me", "com.whatsapp", "com.whatsapp.w4b"],
  },
  {
    source: "facebook",
    hosts: ["facebook.com", "fb.com", "fb.me", "com.facebook.katana", "com.facebook.orca"],
  },
  { source: "instagram", hosts: ["instagram.com", "com.instagram.android"] },
  { source: "x", hosts: ["x.com", "twitter.com", "t.co", "com.twitter.android"] },
  { source: "telegram", hosts: ["t.me", "telegram.org", "telegram.me", "org.telegram.messenger"] },
  { source: "tiktok", hosts: ["tiktok.com"] },
  { source: "snapchat", hosts: ["snapchat.com"] },
  { source: "youtube", hosts: ["youtube.com", "youtu.be"] },
  { source: "reddit", hosts: ["reddit.com"] },
  { source: "github", hosts: ["github.com"] },
  { source: "tabbio", hosts: ["tabbio.com"] },
];

function matchesHost(host: string, domain: string) {
  return host === domain || host.endsWith(`.${domain}`);
}

/**
 * A referrer host as a named source (`google`, `linkedin`, `email`…), or the
 * bare host when it is not one we name. Null for no host.
 */
export function classifyReferrerHost(value: string | null | undefined): string | null {
  const host = value?.trim().toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
  if (!host) return null;
  for (const rule of HOST_RULES) {
    if (rule.hosts.some((domain) => matchesHost(host, domain))) return rule.source;
  }
  // google.com, google.co.uk, google.ae… but not docs/mail/drive.google.com,
  // which the rules above or the host fallback already speak for.
  if (/^google(?:\.[a-z]{2,3}){1,2}$/.test(host)) return "google";
  if (/^yandex(?:\.[a-z]{2,3}){1,2}$/.test(host)) return "yandex";
  return host;
}

/**
 * Tags that say how a link left the owner's hands, not where it was put.
 * A copied link pasted into a LinkedIn post arrives with LinkedIn as its
 * referrer, and LinkedIn is the better answer; the tag only speaks when the
 * visit brought no referrer of its own.
 */
const WEAK_TAGS = new Set(["copy", "share"]);

/**
 * The one source a visit is counted under. `utmSource` is the stored column,
 * which also carries the `ref` a share button set (see the API's recorder).
 */
export function resolveCvTrafficSource(input: {
  utmSource?: string | null;
  referrerHost?: string | null;
}): string {
  const fromReferrer = classifyReferrerHost(input.referrerHost);
  const tagged = input.utmSource?.trim().toLowerCase();
  if (tagged) {
    // `utm_source=linkedin.com` names the same place as `utm_source=linkedin`.
    const named = tagged.includes(".") ? classifyReferrerHost(tagged) : normalizeTrafficToken(tagged);
    if (named && !(WEAK_TAGS.has(named) && fromReferrer)) return named;
  }
  return fromReferrer ?? "direct";
}
